import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/Button';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Clock, ShieldAlert, RefreshCw, Sparkles, LogOut } from 'lucide-react';
import { settingsService, PlanUsage } from '@/services/settings.service';

export default function SubscriptionBlocked() {
  const { logout, user } = useAuth();
  const [data, setData] = useState<PlanUsage | null>(null);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    // Try to fetch plan info — may still 403 if guard rejects, that's fine.
    settingsService.plan().then(setData).catch(() => {});
  }, []);

  const status = data?.company.status as 'expired' | 'suspended' | undefined;
  const expiresAt = data?.company.expiresAt ? new Date(data.company.expiresAt) : null;

  const onRetry = async () => {
    setRetrying(true);
    try {
      const d = await settingsService.plan();
      setData(d);
      // If status is no longer expired/suspended, send them home.
      if (d.company.status === 'active' || d.company.status === 'trial') {
        window.location.replace('/dashboard');
      }
    } catch { /* still blocked */ } finally { setRetrying(false); }
  };

  const isSuspended = status === 'suspended';
  const headline = isSuspended ? 'Your subscription is suspended.' : 'Your subscription has expired.';
  const detail = isSuspended
    ? 'Access has been temporarily paused by your administrator. Contact them to learn more or to reactivate.'
    : `Your access to SlightorCRM has been paused${expiresAt ? ` since ${expiresAt.toLocaleDateString()}` : ''}. Renew your plan to continue.`;

  return (
    <div className="relative min-h-screen overflow-hidden bg-bg bg-paper-noise">
      <div className="pointer-events-none absolute -right-32 -top-40 h-[28rem] w-[28rem] rounded-full bg-gold-gradient-soft blur-3xl" />
      <div className="pointer-events-none absolute -left-24 bottom-[-10rem] h-[24rem] w-[24rem] rounded-full bg-gold-gradient-soft blur-3xl" />

      <div className="absolute right-5 top-5 z-20"><ThemeToggle /></div>
      <div className="absolute left-7 top-7 z-20 flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gold-gradient text-on-primary shadow-glow">
          <Sparkles size={16} strokeWidth={2.5} />
        </div>
        <div className="font-display text-[15px] font-bold tracking-tight text-text-primary">SlightorCRM</div>
      </div>

      <div className="relative flex min-h-screen items-center justify-center px-6 py-16">
        <div className="w-full max-w-xl rounded-3xl border border-border bg-surface/85 p-8 shadow-elevated backdrop-blur-xl sm:p-10">
          <div className="inline-flex items-center gap-2 rounded-full border border-danger/30 bg-danger-soft px-3 py-1">
            {isSuspended
              ? <ShieldAlert size={13} className="text-danger" />
              : <Clock size={13} className="text-danger" />}
            <span className="text-xs font-semibold uppercase tracking-wide text-danger">
              {isSuspended ? 'Account suspended' : 'Subscription expired'}
            </span>
          </div>

          <h1 className="mt-5 font-display text-[28px] font-bold leading-tight tracking-tight text-text-primary">
            {headline}
          </h1>
          <p className="mt-3 text-[15px] leading-relaxed text-text-secondary">{detail}</p>

          {data && (
            <div className="mt-6 grid grid-cols-2 gap-3 rounded-2xl border border-border bg-surface-alt/40 p-4 text-sm">
              <div>
                <div className="text-2xs uppercase tracking-wide text-text-muted">Workspace</div>
                <div className="font-semibold text-text-primary">{data.company.name}</div>
              </div>
              {expiresAt && (
                <div>
                  <div className="text-2xs uppercase tracking-wide text-text-muted">Expired on</div>
                  <div className="font-semibold text-text-primary">{expiresAt.toLocaleDateString()}</div>
                </div>
              )}
              {data.plan && (
                <div className="col-span-2">
                  <div className="text-2xs uppercase tracking-wide text-text-muted">Plan</div>
                  <div className="font-semibold text-text-primary">{data.plan.displayName}</div>
                </div>
              )}
            </div>
          )}

          <div className="mt-7 flex flex-col gap-2 sm:flex-row">
            <Button onClick={onRetry} loading={retrying} iconLeft={<RefreshCw size={15} />}>
              Check again
            </Button>
            <Button variant="secondary" onClick={() => { logout(); window.location.replace('/login'); }} iconLeft={<LogOut size={15} />}>
              Sign out
            </Button>
          </div>

          <div className="mt-8 border-t border-border pt-5 text-xs text-text-muted">
            {user?.role === 'company_admin'
              ? 'As a workspace admin, contact your SlightorCRM account manager to renew or unsuspend.'
              : 'Contact your workspace admin to restore access.'}
          </div>
        </div>
      </div>
    </div>
  );
}
